import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '@/constants/api';
import { ROUTES } from '@/constants/routes';
import { STORAGE_KEYS } from '@/constants/storage';
import type { ApiErrorBody, ApiResponse } from '@/types/api';
import { tokenStorage } from '@/services/tokenStorage';
import { refreshTaskSocketAuth } from '@/services/taskRealtime';

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return null;

  try {
    const { data } = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
      `${API_BASE_URL}/api/auth/refresh`,
      { refreshToken },
    );

    const user = tokenStorage.getUser();
    if (user) {
      tokenStorage.setTokens({
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken,
        user,
      });
      refreshTaskSocketAuth();
    }

    return data.data.accessToken;
  } catch {
    tokenStorage.clear();
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => {
    if (typeof response.data === 'string' && response.data.trim().startsWith('<')) {
      throw new Error('API request returned HTML instead of JSON. Check backend API server routing or VITE_API_BASE_URL.');
    }
    return response;
  },
  async (error: AxiosError<ApiErrorBody>) => {
    const originalRequest = error.config as RetryConfig | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/auth/login') &&
      !originalRequest.url?.includes('/api/auth/refresh') &&
      !originalRequest.url?.includes('/api/polls/public')
    ) {
      originalRequest._retry = true;

      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      const newToken = await refreshPromise;
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }

      const currentHash = window.location.hash.replace(/^#/, '');
      if (currentHash.startsWith('/tasks/') || currentHash.includes('taskId=')) {
        try {
          sessionStorage.setItem(STORAGE_KEYS.PENDING_DEEP_LINK, currentHash);
        } catch {
          /* ignore */
        }
      }
      window.location.href = `/#${ROUTES.LOGIN}`;
    }

    const message =
      error.response?.data?.message ??
      error.response?.data?.error ??
      error.message ??
      'Something went wrong';

    return Promise.reject(new Error(message));
  },
);

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}
